<template>
  <div
    v-if="show"
    class="modal fade show"
    tabindex="-1"
    role="dialog"
    style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    @click.self="closeModal"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-receipt me-2"></i>
            Transaction Details
            <span v-if="transactionData && transactionData.is_refund" class="refund-badge ms-2">
              REFUNDED
            </span>
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="closeModal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="transactionData" class="transaction-details" :class="{ 'refund-stamped': transactionData.is_refund }">
            <!-- Transaction ID Section -->
            <div class="detail-section">
              <h6 class="section-title">
                <i class="fas fa-hashtag me-2"></i>
                Transaction Information
              </h6>
              <div class="row">
                <div class="col-lg-4 col-md-6">
                  <div class="detail-item">
                    <label>Transaction ID</label>
                    <div class="detail-value transaction-id">
                      {{ transactionData.transaction_id }}
                      <i 
                        class="fas fa-copy copy-icon ms-2" 
                        @click="copyToClipboard(transactionData.transaction_id, 'Transaction ID')"
                        title="Copy Transaction ID"
                      ></i>
                    </div>
                  </div>
                </div>
                <div class="col-lg-4 col-md-6">
                  <div class="detail-item">
                    <label>Reference ID</label>
                    <div class="detail-value">{{ transactionData.reference_id }}</div>
                  </div>
                </div>
                <div class="col-lg-4 col-md-6">
                  <div class="detail-item">
                    <label>Status</label>
                    <div class="detail-value">
                      <span :class="['status-badge', statusClass(transactionData.status)]">
                        {{ transactionData.status }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-lg-4 col-md-6">
                  <div class="detail-item">
                    <label>Source</label>
                    <div class="detail-value">{{ transactionData.source }}</div>
                  </div>
                </div>
                <div class="col-lg-4 col-md-6">
                  <div class="detail-item">
                    <label>Bank</label>
                    <div class="detail-value">{{ transactionData.bank }}</div>
                  </div>
                </div>
                <div class="col-lg-4 col-md-6">
                  <div class="detail-item">
                    <label>Transaction Date & Time</label>
                    <div class="detail-value">{{ formatDateTime(transactionData.transaction_datetime) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Financial Information Section -->
            <div class="detail-section">
              <h6 class="section-title">
                <i class="fas fa-money-bill-wave me-2"></i>
                Financial Information
              </h6>
              <div class="row">
                <div class="col-lg-6 col-md-6">
                  <div class="detail-item">
                    <label>Amount</label>
                    <div class="detail-value amount">IDR {{ formatNumber(transactionData.amount) }}</div>
                  </div>
                </div>
                <div class="col-lg-6 col-md-6">
                  <div class="detail-item">
                    <label>Admin Fee</label>
                    <div class="detail-value">IDR {{ formatNumber(transactionData.admin_fee) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Account Information Section -->
            <div class="detail-section">
              <h6 class="section-title">
                <i class="fas fa-user me-2"></i>
                Account Information
              </h6>
              <div class="row">
                <div class="col-lg-6 col-md-6">
                  <div class="detail-item">
                    <label>Account Name</label>
                    <div class="detail-value">{{ transactionData.account_name }}</div>
                  </div>
                </div>
                <div class="col-lg-6 col-md-6">
                  <div class="detail-item">
                    <label>Account Number</label>
                    <div class="detail-value">{{ maskAccountNumber(transactionData.account_number, transactionData.bank) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Date & Time Information Section -->
            <div class="detail-section">
              <h6 class="section-title">
                <i class="fas fa-clock me-2"></i>
                Date & Time Information
              </h6>
              <div class="row">
                <div class="col-lg-6 col-md-6">
                  <div class="detail-item">
                    <label>Created At</label>
                    <div class="detail-value">{{ formatDateTime(transactionData.created_at) }}</div>
                  </div>
                </div>
                <div class="col-lg-6 col-md-6" v-if="transactionData.is_refund && transactionData.refund_date">
                  <div class="detail-item">
                    <label>Refund Date</label>
                    <div class="detail-value">{{ formatDateTime(transactionData.refund_date) }}</div>
                  </div>
                  <div class="detail-item">
                    <label>UUID</label>
                    <div class="detail-value uuid-link" @click="navigateToBalance">
                      {{ transactionData.uuid }}
                      <i class="fas fa-external-link-alt ms-2"></i>
                    </div>
                    <small class="text-muted">Click to view in Balance page</small>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatNumber as formatNumberUtil, maskAccountNumber } from "@/utils/param-utils";

export default {
  name: "TransactionDetailModal",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    transactionData: {
      type: Object,
      default: null,
    },
  },
  computed: {
    maskAccountNumber() {
      return maskAccountNumber;
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    statusClass(status) {
      switch (status.toLowerCase()) {
        case "success":
          return "success";
        case "failed":
          return "failed";
        case "pending":
          return "waiting";
        case "processing":
          return "draft";
        default:
          return "";
      }
    },
    formatNumber(value) {
      return formatNumberUtil(value);
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return "-";
      const date = new Date(dateTimeString);
      return date.toLocaleString("id-ID", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone: "Asia/Jakarta",
      });
    },
    async copyToClipboard(text, label) {
      try {
        await navigator.clipboard.writeText(text);
        this.$swal({
          title: "Copied!",
          text: `${label} has been copied to clipboard`,
          icon: "success",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } catch (error) {
        console.error("Failed to copy:", error);
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        this.$swal({
          title: "Copied!",
          text: `${label} has been copied to clipboard`,
          icon: "success",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      }
    },
    navigateToBalance() {
      if (this.transactionData && this.transactionData.uuid) {
        // Navigate to balance page with the UUID as reference
        this.$router.push({
          name: "Balance",
          query: { ref_id: this.transactionData.uuid },
        });
        this.closeModal();
      }
    },
  },
};
</script>

<style scoped>
.modal {
  z-index: 1055;
}

.modal-content {
  border: none;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.modal-header {
  background: #43BB75;
  color: white;
  border-radius: 12px 12px 0 0;
  border-bottom: none;
  padding: 1rem 1.5rem;
}

.modal-title {
  font-weight: 600;
  font-size: 1.25rem;
  margin: 0;
  display: flex;
  align-items: center;
}

.refund-badge {
  background-color: #dc3545;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}

.btn-close {
  filter: invert(1);
  opacity: 0.8;
}

.btn-close:hover {
  opacity: 1;
}

.modal-body {
  padding: 1.5rem;
  max-height: 85vh;
  overflow-y: auto;
}

.detail-section {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

.detail-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.section-title {
  color: #495057;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.detail-item {
  margin-bottom: 0.5rem;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item label {
  font-weight: 600;
  color: #6c757d;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
  display: block;
}

.detail-value {
  color: #212529;
  font-size: 0.9rem;
  word-break: break-word;
  line-height: 1.3;
}

.transaction-id {
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  font-size: 0.85rem;
  background-color: #f8f9fa;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.copy-icon {
  color: #6c757d;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease-in-out;
}

.copy-icon:hover {
  color: #007bff;
  transform: scale(1.1);
}

.amount {
  font-weight: 600;
  color: #28a745;
  font-size: 1.1rem;
}

.status-badge {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-block;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.success {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.failed {
  background-color: #f8d7da;
  color: #721c24;
}

.status-badge.waiting {
  background-color: #fff3cd;
  color: #856404;
}

.status-badge.draft {
  background-color: #e2e3e5;
  color: #383d41;
}

.uuid-link {
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  font-size: 0.9rem;
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease-in-out;
}

.uuid-link:hover {
  background-color: #e9ecef;
  color: #0056b3;
}

.modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 0.75rem 1.5rem;
  background-color: #f8f9fa;
  border-radius: 0 0 12px 12px;
}

/* Large screen optimizations */
@media (min-width: 1400px) {
  .modal-dialog {
    max-width: 90vw;
  }
}

@media (min-width: 1200px) {
  .modal-dialog {
    max-width: 85vw;
  }
}

/* Refund Stamp Effect */
.transaction-details.refund-stamped {
  position: relative;
}

.transaction-details.refund-stamped::before {
  content: "REFUNDED";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);
  font-size: 5rem;
  font-weight: 900;
  color: rgba(220, 53, 69, 0.25);
  z-index: 10;
  pointer-events: none;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.3em;
  white-space: nowrap;
  user-select: none;
  font-family: Arial, sans-serif;
}

/* Ensure content is above the stamp */
.transaction-details.refund-stamped .detail-section {
  position: relative;
  z-index: 2;
  background-color: rgba(255, 255, 255, 0.85);
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  backdrop-filter: blur(1px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-dialog {
    margin: 0.5rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .modal-header {
    padding: 0.75rem 1rem;
  }
  
  .modal-footer {
    padding: 0.5rem 1rem;
  }
  
  .detail-section {
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
  }
  
  .section-title {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
  
  .detail-value {
    font-size: 0.85rem;
  }
  
  .detail-item label {
    font-size: 0.75rem;
  }
  
  .transaction-details.refund-stamped::before {
    font-size: 3rem;
    color: rgba(220, 53, 69, 0.3);
  }
  
  .transaction-details.refund-stamped .detail-section {
    padding: 0.5rem;
    margin-bottom: 0.5rem;
  }
}
</style>
